import { ec2InstancesLabel } from '../classification';
import type { CoverageGrouping } from './constants';
import type { CoverageCard, CoverageInstance, CoverageWrapper } from './types';

function buildCoverageGroupsRaw(instances: CoverageInstance[], mode: CoverageGrouping): CoverageWrapper[] {
  if (mode === 'region') {
    const wrappers = new Map<string, CoverageWrapper>();
    instances.forEach((inst) => {
      const wrapKey = inst.cluster ? 'Kubernetes' : ec2InstancesLabel;
      if (!wrappers.has(wrapKey)) {
        wrappers.set(wrapKey, {
          name: wrapKey,
          kind: inst.cluster ? 'k8s' : 'ec2',
          cards: [],
        });
      }
      const w = wrappers.get(wrapKey)!;
      const region = inst.region || '(unknown)';
      let card = w.cards.find((c) => c.name === region);
      if (!card) {
        card = { name: region, kind: 'region', instances: [] };
        w.cards.push(card);
      }
      card.instances.push(inst);
    });
    return [...wrappers.values()].map((w) => ({
      ...w,
      cards: [...w.cards].sort((a, b) => b.instances.length - a.instances.length),
    }));
  }

  const wrappers = new Map<string, CoverageWrapper>();
  instances.forEach((inst) => {
    const wrapKey = inst.cluster || ec2InstancesLabel;
    if (!wrappers.has(wrapKey)) {
      wrappers.set(wrapKey, {
        name: wrapKey,
        kind: inst.cluster ? 'k8s' : 'ec2',
        cards: [],
      });
    }
    const w = wrappers.get(wrapKey)!;
    const cardKey = inst.cluster ? inst.nodeGroup || '(No Group)' : ec2InstancesLabel;
    let card = w.cards.find((c) => c.name === cardKey);
    if (!card) {
      card = { name: cardKey, kind: 'nodeGroup', instances: [] };
      w.cards.push(card);
    }
    card.instances.push(inst);
  });

  return [...wrappers.values()]
    .sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'k8s' ? -1 : 1;
      const aT = a.cards.reduce((s, c) => s + c.instances.length, 0);
      const bT = b.cards.reduce((s, c) => s + c.instances.length, 0);
      return bT - aT;
    })
    .map((w) => ({
      ...w,
      cards: [...w.cards].sort((a, b) => b.instances.length - a.instances.length),
    }));
}

export function buildCoverageGroups(
  instances: CoverageInstance[],
  mode: CoverageGrouping,
  groupByAccount: boolean
): CoverageWrapper[] {
  if (groupByAccount) {
    const acctMap = new Map<string, CoverageInstance[]>();
    instances.forEach((i) => {
      const k = i.accountName || i.accountId || '(unknown)';
      if (!acctMap.has(k)) acctMap.set(k, []);
      acctMap.get(k)!.push(i);
    });
    const all: CoverageWrapper[] = [];
    [...acctMap.keys()].sort().forEach((acctName) => {
      const sub = buildCoverageGroupsRaw(acctMap.get(acctName)!, mode);
      sub.forEach((w) => {
        all.push({
          ...w,
          accountName: acctName,
          name: `${acctName} · ${w.name}`,
        });
      });
    });
    return all;
  }
  return buildCoverageGroupsRaw(instances, mode);
}

export function enrichCoverageWrappers(wrappers: CoverageWrapper[]): CoverageWrapper[] {
  const enriched = wrappers.map((w) => {
    const cards = w.cards.map((card) => {
      const installCounts = { autoscaler: 0, sensor: 0, nothing: 0 };
      let supportedSize = 0;
      let totalSize = 0;
      card.instances.forEach((i) => {
        installCounts[i.install]++;
        supportedSize += i.supportedSize || 0;
        totalSize += i.totalSize || 0;
      });
      const sortedInstances = [...card.instances].sort(
        (a, b) => (b.supportedSize || 0) - (a.supportedSize || 0)
      );
      return {
        ...card,
        instances: sortedInstances,
        installCounts,
        supportedSize,
        totalSize,
      };
    });

    const installCounts = { autoscaler: 0, sensor: 0, nothing: 0 };
    let totalInstances = 0;
    let supportedSize = 0;
    let totalSize = 0;
    cards.forEach((c) => {
      (Object.keys(installCounts) as Array<keyof typeof installCounts>).forEach((k) => {
        installCounts[k] += c.installCounts?.[k] ?? 0;
      });
      totalInstances += c.instances.length;
      supportedSize += c.supportedSize ?? 0;
      totalSize += c.totalSize ?? 0;
    });

    const sortedCards = [...cards].sort((a, b) => (b.supportedSize ?? 0) - (a.supportedSize ?? 0));

    return {
      ...w,
      cards: sortedCards,
      installCounts,
      totalInstances,
      supportedSize,
      totalSize,
    };
  });

  return [...enriched].sort((a, b) => (b.supportedSize ?? 0) - (a.supportedSize ?? 0));
}
